import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef  } from '@angular/core';
import { MediaService } from '@core/services/media.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
    selector: 'sfx-regional-settings',
    templateUrl: './regional-settings.component.html',
    styleUrls: ['./regional-settings.component.scss']
})
export class RegionalSettingsComponent implements OnInit, OnDestroy {
    currency: any;
    theme: any;
    language: any;
    ampmNotation: any;
    firstDayOfWeek: any;
    currencies: any;
    timeZones: any;
    timeZone: any;

    // The options for the select element
    timeNotations: any = [];

    // The options for the select element
    startDays: any = [];

    private langChangeSubscription?: Subscription;

    constructor(
        @Inject('modelService') public modelService: any,
        @Inject('currenciesService') public currenciesService: any,
        @Inject('timeZoneService') public timeZoneService: any,
        @Inject('teamService') public teamService: any,
        @Inject('userSettingsService') public userSettingsService: any,
        @Inject('localeService') public localeService: any,
        @Inject('flagsService') public flagsService: any,
        @Inject('translateService') public translateService: any,
        @Inject('stateService') public stateService: any,
        public media: MediaService,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef
    ) {
        this.updateTimeNotations();
        this.updateStartDays();
    }

    ngOnInit(): void {
        this.currency = this.modelService.me.team.currency;
        this.theme = this.modelService.me.theme;
        this.language = this.modelService.me.language;
        if (this.modelService.me.am_pm_notation) {
            this.ampmNotation = this.timeNotations[1];
        }
        else {
            this.ampmNotation = this.timeNotations[0];
        }

        this.firstDayOfWeek = this.startDays.find((day: any) => day.value === this.modelService.me.first_day_of_week);
        this.localeService.firstDayOfWeek = this.firstDayOfWeek.value;
        this.get();

        // Subscribe to language changes to update translations without reloading
        this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
            this.updateTimeNotations();
            this.updateStartDays();
            // Restore selected values after updating arrays
            if (this.modelService.me.am_pm_notation) {
                this.ampmNotation = this.timeNotations[1];
            }
            else {
                this.ampmNotation = this.timeNotations[0];
            }

            this.firstDayOfWeek = this.startDays.find((day: any) => day.value === this.modelService.me.first_day_of_week);
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        if (this.langChangeSubscription) {
            this.langChangeSubscription.unsubscribe();
        }
    }

    private updateTimeNotations(): void {
        this.timeNotations = [
            {
                id: 0,
                notation: this.translate.instant('24-hour clock'),
                value: false
            },
            {
                id: 1,
                notation: this.translate.instant('12-hour clock (AM-PM)'),
                value: true
            }
        ];
    }

    private updateStartDays(): void {
        this.startDays = [
            {
                id: 0,
                day: this.translate.instant('Sunday'),
                value: 0
            },
            {
                id: 1,
                day: this.translate.instant('Monday'),
                value: 1
            },
            {
                id: 2,
                day: this.translate.instant('Saturday'),
                value: 6
            }
        ];
    }

    public get = ():any => {

        this.currenciesService.get().then((response: any) => {

            this.currencies = response.data;
        });
        return this.timeZoneService.get().then((response: any) => {

            this.timeZones = response.data;
            this.timeZone = this.timeZones.find((timeZone: any) => timeZone.value === this.modelService.me.team.time_zone) || this.timeZones.find((timeZone: any) => timeZone.value === 'UTC');
        });
    }

    public showTheme = () => {

        return this.flagsService.get('darkmode') === true;
    }

    public showMultipleLanguages = () => {

        return this.flagsService.get('multiplelanguages') === true;
    }

    public savePersonalSettings = ():any => {
        const personalSettings = {
            theme: this.theme,
            language: this.language,
            am_pm_notation: this.ampmNotation.value,
            first_day_of_week: Number.parseInt(this.firstDayOfWeek.value)
        };

        if (this.theme && (this.modelService.me.theme !== this.theme)) {
            personalSettings.theme = this.theme;
            this.modelService.me.theme = this.theme;
        }

        if (this.language && (this.modelService.me.language !== this.language)) {
            personalSettings.language = this.language;
            this.modelService.me.language = this.language;

            this.translateService.use(this.languageCodes[this.language]);
            this.translate.use(this.languageCodes[this.language]);
        }

        if (this.ampmNotation && (this.modelService.me.am_pm_notation !== this.ampmNotation.value)) {
            personalSettings.am_pm_notation = this.ampmNotation.value;
            this.modelService.me.am_pm_notation = this.ampmNotation.value;
        }

        if (this.firstDayOfWeek && (this.modelService.me.first_day_of_week !== this.firstDayOfWeek.value)) {
            personalSettings.first_day_of_week = Number.parseInt(this.firstDayOfWeek.value);
            this.modelService.me.first_day_of_week = Number.parseInt(this.firstDayOfWeek.value);
            this.localeService.firstDayOfWeek = Number.parseInt(this.firstDayOfWeek.value);
        }

        // Update UserSettings with the changed values
        return this.userSettingsService.update(personalSettings);
    }

    public saveCurrency = ():any => {
        if (this.modelService.me.team.currency !== this.currency) {
            this.modelService.me.team.currency = this.currency;
            return this.teamService.updateCurrency(this.currency.id);
        }
    }

    public saveTimeZone = ():any => {
        if (this.modelService.me.team.time_zone !== this.timeZone.value) {
            this.modelService.me.team.time_zone = this.timeZone.value;
            return this.teamService.update({ time_zone: this.timeZone.value });
        }
    }

    public compareCurrencies = (currencyOne: any, currencyTwo: any): boolean => {
        return currencyOne && currencyTwo ? currencyOne.id === currencyTwo.id : currencyOne === currencyTwo;
    }

    public languageOptions: any = {
        'en': { flag: '🇺🇸', name: 'English' },
        'fr': { flag: '🇫🇷', name: 'Français' },
        'de': { flag: '🇩🇪', name: 'Deutsch' },
        'it': { flag: '🇮🇹', name: 'Italiano' },
        'es': { flag: '🇪🇸', name: 'Español' },
        'nl': { flag: '🇳🇱', name: 'Nederlands' },
        'br': { flag: '🇧🇷', name: 'Português (Brasil)' },
        'pt': { flag: '🇵🇹', name: 'Português (Portugal)' }
    };

    public languageCodes: any = {
        'en': 'en',
        'fr': 'fr',
        'de': 'de',
        'it': 'it',
        'es': 'es',
        'nl': 'nl',
        'br': 'pt-BR',
        'pt': 'pt-PT'
    };

    public getLanguageDisplay = (languageCode: string): any => {
        return this.languageOptions[languageCode] || { flag: '🇺🇸', name: 'English' };
    }

    public getTimeNotationText = (timeNotation: any): string => {
        return timeNotation.notation;
    }

    public getStartDayText = (startDay: any): string => {
        return startDay.day;
    }
}
